import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import * as consts from 'config/consts';
import { GetUserBrokerResultItem } from 'config/api';
import { getGroup, isLoaded, Grouping } from 'hooks/useGrouping';
import UserBrokerItem from './UserBrokerItem';
import { groupBy } from '../utils/utils';

type UserBrokerListProps = {
  brokerItems: Array<GetUserBrokerResultItem> | null | undefined;
  onBrokerClick?: (item: GetUserBrokerResultItem) => void;
  onChangeState?: (item: GetUserBrokerResultItem) => void;
  editable: boolean;
  groupingState: Grouping;
};

const UserBrokerList: React.FC<UserBrokerListProps> = ({
  brokerItems,
  onBrokerClick,
  onChangeState,
  editable,
  groupingState,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const { pathname, search } = useLocation();
  const newSecurityAccountsPathRegex = /^\/securityaccounts(?:\?account=(?<query>.*))?$/;

  const parsePage = (pathname: string): string | null => {
    const regex = newSecurityAccountsPathRegex;
    const result = regex.exec(pathname);
    if (!result || result.groups === undefined) return null;
    const query =
      typeof String(result.groups?.query) === 'string'
        ? result.groups?.query
        : null;
    return query;
  };

  const requestQuery = parsePage(pathname + search);

  const noBrokers = (
    <div
      className="bg-white rounded mb-4 border-gray-300 border-solid"
      key="noBrokers">
      <div className="flex justify-center items-center">
        <div className="font-bold text-gray-700 py-10">
          口座登録がありません
        </div>
      </div>
    </div>
  );
  if (!isLoaded(groupingState)) return null;

  /* 
    BrokerID(※1)でグルーピングして表示する
    手入力についてはグルーピングは行わない(BrokerID: Account + : + Type としている)

    ただし、同一証券会社でもBrokerIDが集約サービス毎に異なるので、手入力以外は、BrokerNameでグルーピングしている
    HACK: BrokerNameだと厳密性が低いので、うまい方法を考える必要あり
  */
  if (!brokerItems)
    return (
      <>
        <div></div>
      </>
    );

  if (brokerItems.length === 0)
    return (
      <>
        <div>{noBrokers}</div>
      </>
    );

  const brokers_ = groupBy(brokerItems, x =>
    x.AggregateSource === consts.AggregateSourceTypes.手入力
      ? x.BrokerID
      : x.BrokerName
  );

  const sortedBrokers = brokers_
    .sort(([_, firstList], second) => {
      const isManualBroker = firstList.some(
        list => list.AggregateSource === consts.AggregateSourceTypes.手入力
      );

      if (isManualBroker) {
        return 1;
      } else {
        return -1;
      }
    })
    .sort(([firstBrokerId, firstList], [secondBrokerId, secondList]) => {
      const isFirstListCooperationMonoOrTecotech = firstList.some(
        list =>
          list.AggregateSource === consts.AggregateSourceTypes.MONO ||
          list.AggregateSource === consts.AggregateSourceTypes.テコテック
      );

      const isSecondListCooperationMoneytree = secondList.some(
        list =>
          list.AggregateSource === consts.AggregateSourceTypes.マネーツリー
      );

      if (
        isFirstListCooperationMonoOrTecotech &&
        isSecondListCooperationMoneytree
      ) {
        return -1;
      } else {
        return 1;
      }
    })
    .map(([brokerId, list]) => {
      const sortList = list.sort((first, second) => {
        const isFirstBrokerMomoOrTecotech =
          first.AggregateSource === consts.AggregateSourceTypes.MONO ||
          first.AggregateSource === consts.AggregateSourceTypes.テコテック;

        const isSecondBrokerMomoOrTecotech =
          second.AggregateSource === consts.AggregateSourceTypes.MONO ||
          second.AggregateSource === consts.AggregateSourceTypes.テコテック;

        if (!isFirstBrokerMomoOrTecotech && isSecondBrokerMomoOrTecotech) {
          return 1;
        } else if (
          isFirstBrokerMomoOrTecotech &&
          !isSecondBrokerMomoOrTecotech
        ) {
          return -1;
        } else {
          return 0;
        }
      });

      return [brokerId, sortList] as [string, GetUserBrokerResultItem[]];
    });

  const isExistError900 = brokerItems.some(item => item.Status === '900');

  const brokers = sortedBrokers.map(([_, list], index) => {
    const brokerName = list[0].BrokerName;

    const elements = list.map((item, i) => {
      return (
        <UserBrokerItem
          group={getGroup(item.Account, groupingState)}
          key={item.Account}
          brokerItem={item}
          brokerItems={brokerItems}
          onBrokerClick={onBrokerClick}
          onChangeState={onChangeState}
          editable={editable}
          getIsShowDisplayableAssetTypeModalButton={_ => false}
          isExistError900={isExistError900}
          index={i}
        />
      );
    });

    const scroll = list.map(item => {
      return item.Account === requestQuery;
    });

    return (
      <>
        {scroll.includes(true) && (
          <div style={{ scrollMarginTop: '60px' }} ref={scrollRef}></div>
        )}
        <div className="ui-card" key={`${index}`}>
          <div className="flex items-center py-2 ui-border-b">
            <div className="flex-grow ml-4 mt-2 text-lg font-bold">
              {brokerName}
            </div>
          </div>
          {elements}
        </div>
      </>
    );
  });

  return <div>{brokers}</div>;
};

export default UserBrokerList;
